import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Coffee, Send, ChevronRight, Loader2, Settings } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from '../components/Sidebar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Agentes do Conselho com suas especialidades
const COUNCIL_AGENTS = [
  { id: 'helena', name: 'Helena', specialty: 'Narrativa', avatar: 'H' },
  { id: 'augusto', name: 'Augusto', specialty: 'Estrutura', avatar: 'A' },
  { id: 'clara', name: 'Clara', specialty: 'Comercial', avatar: 'C' },
  { id: 'theodoro', name: 'Theodoro', specialty: 'Linguagem', avatar: 'T' },
  { id: 'isabel', name: 'Isabel', specialty: 'Emoção', avatar: 'I' },
];

export default function CafeteriaBestSeller() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [question, setQuestion] = useState('');
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentAgent, setCurrentAgent] = useState(0);
  const responsesEndRef = useRef(null);

  // Check admin via role instead of hardcoded email
  const isAdmin = user?.role === 'superadmin' || user?.is_super_admin === true;

  useEffect(() => {
    responsesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [responses]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!question.trim() || loading) return;

    setLoading(true);
    setResponses([]);

    try {
      // Chamar o endpoint do Conselho existente
      const response = await axios.post(
        `${API_URL}/api/council/consult`,
        { question: question.trim() },
        { withCredentials: true }
      );

      // Simular respostas sequenciais de múltiplos agentes
      const agentResponses = response.data.responses || [];
      
      // Mostrar respostas uma a uma com delay
      for (let i = 0; i < agentResponses.length; i++) {
        await new Promise(resolve => setTimeout(resolve, 800));
        setResponses(prev => [...prev, agentResponses[i]]);
        setCurrentAgent(i + 1);
      }

    } catch (error) {
      console.error('Error consulting council:', error);
      // Fallback: mostrar resposta genérica
      setResponses([{
        agent: 'Helena',
        specialty: 'Narrativa',
        message: 'O Conselho está meditando sobre sua pergunta. Tente novamente em alguns instantes.'
      }]);
    } finally {
      setLoading(false);
      setCurrentAgent(0);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 relative overflow-hidden">
        {/* Background da Cafeteria - CSS puro */}
        <div className="absolute inset-0 cafeteria-bg" />
        
        {/* Overlay gradiente */}
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950/90 via-stone-900/70 to-stone-900/50" />
        
        {/* Conteúdo */}
        <div className="relative z-10 min-h-screen flex flex-col">
          {/* Header */}
          <header className="p-6 md:p-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-2">
              <Coffee className="w-8 h-8 text-amber-500" />
              <h1 className="text-2xl md:text-3xl font-serif text-stone-100">
                Cafeteria Best Seller
              </h1>
            </div>
            <p className="text-stone-400 text-sm md:text-base">
              O lugar de conversas com perspectivas
            </p>
          </header>

          {/* Área de conversa */}
          <div className="flex-1 flex flex-col md:flex-row gap-6 p-4 md:p-8 max-w-6xl mx-auto w-full">
            {/* Lado do Escritor */}
            <div className="md:w-1/2 flex flex-col">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-amber-500">✍</span>
                <span className="text-stone-300 font-medium uppercase text-sm tracking-wider">Escritor</span>
              </div>
              
              <div className="bg-stone-900/60 backdrop-blur-sm rounded-xl border border-stone-700/50 p-4 flex-1">
                <p className="text-stone-400 text-sm mb-4">
                  O que está travando sua escrita hoje?
                </p>
                
                <form onSubmit={handleSubmit} className="flex flex-col h-full">
                  <Textarea
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    placeholder="Descreva seu bloqueio, dúvida ou desafio criativo..."
                    className="flex-1 min-h-[150px] bg-stone-800/50 border-stone-700/50 text-stone-100 placeholder:text-stone-500 resize-none"
                    disabled={loading}
                  />
                  
                  <Button
                    type="submit"
                    disabled={loading || !question.trim()}
                    className="mt-4 bg-amber-600/20 hover:bg-amber-600/30 text-amber-500 border border-amber-600/30"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Consultando...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Enviar ao Conselho
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>

            {/* Lado do Conselho */}
            <div className="md:w-1/2 flex flex-col">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-amber-500">☕</span>
                <span className="text-stone-300 font-medium uppercase text-sm tracking-wider">Conselho</span>
              </div>
              
              <div className="bg-stone-900/40 backdrop-blur-sm rounded-xl border border-stone-700/30 p-4 flex-1 overflow-y-auto max-h-[500px]">
                {responses.length === 0 && !loading ? (
                  <div className="h-full flex items-center justify-center text-stone-500 text-center">
                    <p>As respostas do conselho aparecerão aqui...</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {responses.map((resp, idx) => (
                      <div 
                        key={idx}
                        className="bg-stone-800/60 rounded-lg p-4 border border-stone-700/30 animate-fadeIn"
                        style={{ animationDelay: `${idx * 0.2}s` }}
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-serif text-amber-500">{resp.agent}</span>
                              <span className="text-stone-500 text-xs">{resp.specialty}</span>
                            </div>
                            <p className="text-stone-300 text-sm leading-relaxed">
                              {resp.message}
                            </p>
                          </div>
                          <div className="text-stone-600 font-serif italic text-sm">
                            {resp.agent?.charAt(0) || '?'}
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {loading && (
                      <div className="flex items-center gap-2 text-stone-500 text-sm">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>
                          {COUNCIL_AGENTS[currentAgent]?.name || 'O Conselho'} está pensando...
                        </span>
                      </div>
                    )}
                    
                    <div ref={responsesEndRef} />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Botão Bastidores do Conselho */}
          <div className="p-6 flex justify-center">
            <Button
              onClick={() => navigate('/conselho-agentes')}
              variant="outline"
              className="border-stone-700 text-stone-400 hover:text-stone-200 hover:border-stone-500"
            >
              {isAdmin ? <Settings className="w-4 h-4 mr-2" /> : null}
              Bastidores do Conselho
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {/* Elemento decorativo - xícara de café */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 opacity-30 pointer-events-none">
            <div className="coffee-cup">
              <div className="steam" />
              <div className="steam steam-2" />
              <div className="steam steam-3" />
            </div>
          </div>
        </div>

        <style jsx="true">{`
          .cafeteria-bg {
            background: 
              radial-gradient(ellipse at 30% 20%, rgba(120, 53, 15, 0.15) 0%, transparent 50%),
              radial-gradient(ellipse at 70% 80%, rgba(180, 83, 9, 0.1) 0%, transparent 40%),
              linear-gradient(180deg, #1c1917 0%, #0c0a09 100%);
          }

          @keyframes fadeIn {
            from { 
              opacity: 0; 
              transform: translateY(10px);
            }
            to { 
              opacity: 1;
              transform: translateY(0);
            }
          }

          .animate-fadeIn {
            animation: fadeIn 0.5s ease-out forwards;
            opacity: 0;
          }

          .coffee-cup {
            width: 60px;
            height: 50px;
            background: linear-gradient(135deg, #57534e 0%, #44403c 100%);
            border-radius: 0 0 30px 30px;
            position: relative;
          }

          .coffee-cup::after {
            content: '';
            position: absolute;
            right: -15px;
            top: 10px;
            width: 20px;
            height: 25px;
            border: 3px solid #57534e;
            border-radius: 0 10px 10px 0;
            border-left: none;
          }

          .steam {
            position: absolute;
            top: -20px;
            left: 50%;
            width: 8px;
            height: 20px;
            background: rgba(255,255,255,0.1);
            border-radius: 50%;
            animation: steam 2s ease-in-out infinite;
          }

          .steam-2 {
            left: 30%;
            animation-delay: 0.5s;
          }

          .steam-3 {
            left: 70%;
            animation-delay: 1s;
          }

          @keyframes steam {
            0% {
              transform: translateY(0) scale(1);
              opacity: 0.3;
            }
            50% {
              transform: translateY(-15px) scale(1.2);
              opacity: 0.5;
            }
            100% {
              transform: translateY(-30px) scale(0.8);
              opacity: 0;
            }
          }
        `}</style>
      </main>
    </div>
  );
}
